/** Tap the German text -> its English shows in a tip box beneath it. One tap
 * target per block (never a wall of "i" buttons, per the client). English that
 * already ships with the content is shown instantly and for free; German-only
 * blocks (grammar rules, exam texts) are translated on demand via /translate/
 * and cached. Tapping again hides it. */
import { useState } from "react";
import { translateText } from "../api/translate";
import { useT } from "../i18n";
import { Spinner } from "./ui";

export function TapToTranslate({
  text,
  english,
  className = "",
  block = false,
}: {
  text: string;
  /** Pre-stored English for this block, when the content already carries it. */
  english?: string | null;
  className?: string;
  /** Render the tap target as a full-width block — for paragraph-level German
   *  (passages, rubrics, model answers) so long text wraps like a `<p>` rather
   *  than an inline run. */
  block?: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shown, setShown] = useState<string | null>(english ?? null);
  const [error, setError] = useState(false);

  // Nothing to translate: render no tap target at all. Call sites pass
  // `String(payload.x ?? "")`, so a missing field must not leave an invisible
  // empty button behind (it would also fire a pointless /translate call).
  if (!text.trim()) return null;

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (shown !== null) return; // already have it (pre-stored or fetched earlier)
    setLoading(true);
    setError(false);
    try {
      setShown(await translateText(text));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className={className}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={t("translate.tapHint")}
        className={`cursor-help whitespace-pre-line text-left underline decoration-dotted underline-offset-2 hover:decoration-solid ${block ? "block w-full" : ""}`}
      >
        {text}
      </button>
      {open && (
        <span role="note" className="mt-1 block rounded-control border border-line bg-canvas p-2 text-sm text-muted">
          {loading ? <Spinner size={14} /> : error ? t("translate.error") : shown}
        </span>
      )}
    </span>
  );
}
